/**
 * Billing Service — centralized API client for all billing operations.
 */

import { api } from './api';

const billingService = {
  /** Get current subscription details, usage, and entitlements */
  async getSubscription() {
    const res = await api.get('/billing/subscription');
    return res.data;
  },

  /** Get trial/subscription status for banners */
  async getTrialStatus() {
    const res = await api.get('/billing/trial-status');
    return res.data;
  },

  /** Get all plans with pricing and feature metadata */
  async getPlans() {
    const res = await api.get('/billing/plans/metadata');
    return res.data;
  },

  /** Get usage counts against plan limits */
  async getUsage() {
    const res = await api.get('/billing/usage');
    return res.data;
  },

  /** Create a Stripe Checkout session */
  async createCheckout(planTier, billingInterval = 'monthly') {
    const res = await api.post('/billing/checkout', {
      plan_tier: planTier,
      billing_interval: billingInterval,
    });
    return res.data;
  },

  /** Verify a checkout session completed successfully */
  async verifyCheckout(sessionId) {
    const res = await api.get(`/billing/checkout-status/${sessionId}`);
    return res.data;
  },

  /** Create a Stripe Customer Portal session */
  async createPortal() {
    const res = await api.post('/billing/portal');
    return res.data;
  },

  /** Change plan (upgrade/downgrade) */
  async changePlan(planTier, billingInterval = null) {
    const body = { plan_tier: planTier };
    if (billingInterval) body.billing_interval = billingInterval;
    const res = await api.post('/billing/change-plan', body);
    return res.data;
  },

  /** Cancel subscription at end of billing period */
  async cancelSubscription() {
    const res = await api.post('/billing/cancel');
    return res.data;
  },

  /** Reactivate a subscription scheduled for cancellation */
  async reactivateSubscription() {
    const res = await api.post('/billing/reactivate');
    return res.data;
  },

  /** Get invoice history (cursor-based pagination) */
  async getInvoices({ starting_after = null, limit = 10 } = {}) {
    const params = { limit };
    if (starting_after) params.starting_after = starting_after;
    const res = await api.get('/billing/invoices', { params });
    return res.data;
  },
};

export default billingService;
